import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { X } from "lucide-react";
import { Vehicle } from "./FleetManagementPage";
import { useGetAllBranchesQuery, useGetAllCarModelsQuery, useCreateFleetMutation } from "../../redux/services/fleetManagement";
import { RootState } from "../../redux/services/store";
import { useStaffController } from "../../controllers/useStaffController";
import { toast } from "sonner";
import { getPlanLimitError, usePlanLimit } from "../../hooks/usePlanLimit";
import { getApiErrorMessage } from "../../utils/apiError";
import { usePermission } from "../../utils/permissions";

const BRAND = "#143887";
const BRAND_LIGHT = "#1438871A";

interface Props {
  onClose: () => void;
  onSubmit: (data: Partial<Vehicle>) => void;
  vehicles: Vehicle[];
}

export default function AddVehicleModal({ onClose, onSubmit }: Props) {
  const { can } = usePermission();
  const canAddFleet = can("add fleet");
  const user = useSelector((state: RootState) => state.auth.user);
  const { data: carModels } = useGetAllCarModelsQuery();
  const { data: branchesData, isLoading: branchesLoading } = useGetAllBranchesQuery(
    { user_id: user?.id ?? "" },
    { skip: !user?.id }
  );

  const { staffData } = useStaffController();
  const branchData = branchesData?.data?.branchInfo?.data;
  const [createFleet, { isLoading: isCreating, error }] = useCreateFleetMutation();
  const { handlePlanLimitError } = usePlanLimit();
  const [modelSearch, setModelSearch] = useState("");
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const modelRef = useRef<HTMLDivElement>(null);

  const filteredModels = useMemo(() => {
    const models = carModels?.data || [];

    return models.filter((item: any) =>
      item.model_name
        ?.toLowerCase()
        .includes(modelSearch.toLowerCase())
    );
  }, [carModels, modelSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modelRef.current &&
        !modelRef.current.contains(event.target as Node)
      ) {
        setShowModelDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [form, setForm] = useState({
    type: "", licensePlate: "", vin: "", capacity: "",
    branch: "", model: "", assignedDriver: "", year: "2025",
  });

  const set = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    // if (!form.licensePlate || !form.vin || !form.model) return;
    if (!canAddFleet) {
      toast.error("You don't have permission to add fleet");
      return;
    }

    try {
      const capacity = Number(form.capacity);

      const payload = {
        vehicle_type: form.type,
        license_plate: form.licensePlate,
        vin: form.vin,
        capacity: Number.isNaN(capacity) ? 0 : capacity,
        branch_id: form.branch,
        model_id: form.model,
        user_id: form.assignedDriver,
        company_id: user?.company?.id ?? "",
        status: "pending",
      };

      // console.log(payload);

      const result = await createFleet(payload).unwrap();

      toast.success("Vehicle created successfully!");

      onSubmit({ ...form });

      onClose();
    } catch (err: any) {
      console.error(err);
      if (handlePlanLimitError(err)) return;
      toast.error(getApiErrorMessage(err, "Failed to create vehicle. Please try again."));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: BRAND_LIGHT }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M3 13L5 6H19L21 13" stroke={BRAND} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M3 13H21V18H3V13Z" stroke={BRAND} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="7" cy="18" r="2" stroke={BRAND} strokeWidth="1.5" />
                <circle cx="17" cy="18" r="2" stroke={BRAND} strokeWidth="1.5" />
              </svg>
            </div>
            <p className="font-semibold text-gray-800 text-sm">Add new vehicle</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Vehicle Type <span className="text-red-500">*</span></label>
              {/* <input value={form.type} onChange={(e) => set("type", e.target.value)} placeholder="e.g. Van" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-300" /> */}
              <div className="relative">
                <select
                  value={form.type}
                  onChange={(e) => set("type", e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-300 appearance-none bg-white"
                >
                  <option value="">Select Vehicle Type</option>
                  <option value="Car">Car</option>
                  <option value="Van">Van</option>
                  <option value="Bike">Bike</option>
                  <option value="Lorry">Truck</option>
                </select>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">▾</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">License Plate <span className="text-red-500">*</span></label>
              <input value={form.licensePlate} onChange={(e) => set("licensePlate", e.target.value)} placeholder="WSD12232" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-300" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">VIN</label>
              <input value={form.vin} onChange={(e) => set("vin", e.target.value)} placeholder="1212212" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-300" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Capacity</label>
              <input value={form.capacity} onChange={(e) => set("capacity", e.target.value)} placeholder="e.g. 12 Seats" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-300" />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Branch</label>
            <div className="relative">
              <select
                value={form.branch}
                onChange={(e) => set("branch", e.target.value)}
                disabled={branchesLoading}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none appearance-none bg-white disabled:bg-gray-50"
              >
                <option value="">
                  {branchesLoading ? "Loading branches..." : "Select branch"}
                </option>
                {branchData?.map((branch: any) => {
                  const locationStr = branch?.branch_detail?.branch_name || '';
                  const label = locationStr.charAt(0).toUpperCase() + locationStr.slice(1);
                  const value = branch?.id;
                  return (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  );
                })}
              </select>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">▾</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Model Field */}
            <div ref={modelRef} className="relative">
              <label className="text-xs text-gray-500 mb-1 block">Model <span className="text-red-500">*</span></label>

              <div className="relative">
                <input
                  type="text"
                  value={modelSearch}
                  placeholder="Search or type model"
                  onFocus={() => setShowModelDropdown(true)}
                  onChange={(e) => {
                    const value = e.target.value;
                    setModelSearch(value);
                    set("model", value);
                    setShowModelDropdown(true);
                  }}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-300"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">▾</span>
              </div>


              {showModelDropdown && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto left-0 right-0">
                  {(filteredModels?.length > 0
                    ? filteredModels
                    : carModels?.data || []
                  ).map((item: any) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        set("model", item.model_name);
                        setModelSearch(item.model_name);
                        setShowModelDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-500 hover:text-white transition"
                    >
                      {item.model_name}
                    </button>
                  ))}

                  {modelSearch &&
                    filteredModels?.length === 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          set("model", modelSearch);
                          setShowModelDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50"
                      >
                        Use "{modelSearch}"
                      </button>
                    )}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Assigned Driver</label>
              <div className="relative">
                <select value={form.assignedDriver} onChange={(e) => set("assignedDriver", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none appearance-none bg-white">
                  <option value="">Select driver</option>
                  {staffData?.map((d) => <option key={d.id} value={d.id}>
                    {d.name}
                  </option>)}
                </select>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">▾</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5">
          {/* Plan-limit errors get their own message with an Upgrade button */}
          {error && !getPlanLimitError(error) && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              Failed to create vehicle. Please try again.
            </div>
          )}
          <button
            onClick={handleSubmit}
            disabled={isCreating || !form.licensePlate || !form.model || !canAddFleet}
            title={!canAddFleet ? "You don't have permission to add fleet" : undefined}
            className="w-full py-2.5 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: BRAND }}
          >
            {isCreating ? "Creating Vehicle..." : "Add New Vehicle"}
          </button>
        </div>
      </div>
    </div>
  );
}